import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { VendorMetricsSummary } from '@/types/domain/vendor-management';
import { Users, CheckCircle2, Clock, AlertTriangle, ShieldAlert, XCircle } from 'lucide-react';

interface VendorKpiCardsProps {
  metrics: VendorMetricsSummary;
  onFilterClick?: (statusGroup: string) => void;
}

export const VendorKpiCards: React.FC<VendorKpiCardsProps> = ({ metrics, onFilterClick }) => {
  const cards = [
    {
      title: 'Total Vendors',
      value: metrics.totalVendors,
      icon: Users,
      color: 'text-primary bg-primary/10 border-primary/20',
      groupKey: 'ALL',
      description: 'Master Directory records',
    },
    {
      title: 'Active / Approved',
      value: metrics.activeVendors,
      icon: CheckCircle2,
      color: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20',
      groupKey: 'APPROVED',
      description: 'Eligible for sourcing & POs',
    },
    {
      title: 'Pending Verification',
      value: metrics.pendingVerification,
      icon: Clock,
      color: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
      groupKey: 'PENDING_REVIEW',
      description: 'Awaiting compliance check',
    },
    {
      title: 'Suspended / Blocked',
      value: metrics.suspendedVendors + metrics.blockedVendors,
      icon: XCircle,
      color: 'text-rose-600 bg-rose-500/10 border-rose-500/20',
      groupKey: 'SUSPENDED',
      description: 'Restricted sourcing access',
    },
    {
      title: 'Compliance Expiring',
      value: metrics.complianceExpiring,
      icon: AlertTriangle,
      color: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
      groupKey: 'COMPLIANCE_EXPIRING',
      description: 'Certificates expiring ≤ 30 days',
    },
    {
      title: 'High-Risk Suppliers',
      value: metrics.highRiskVendors,
      icon: ShieldAlert,
      color: 'text-rose-600 bg-rose-500/10 border-rose-500/20',
      groupKey: 'HIGH_RISK',
      description: 'High risk rating flag',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5 font-poppins">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <Card
            key={c.title}
            onClick={() => onFilterClick && onFilterClick(c.groupKey)}
            className="border-border rounded-2xl shadow-2xs hover:shadow-md transition-all cursor-pointer overflow-hidden group"
          >
            <CardContent className="p-3.5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-extrabold text-foreground-secondary uppercase tracking-wider">
                  {c.title}
                </span>
                <div className={`p-2 rounded-xl border ${c.color} group-hover:scale-105 transition-transform`}>
                  <Icon className="h-4 w-4" />
                </div>
              </div>

              <div>
                <span className="text-2xl font-extrabold text-foreground tracking-tight block">
                  {c.value}
                </span>
                <span className="text-[10px] font-bold text-foreground-muted block mt-0.5">
                  {c.description}
                </span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
